import { useFormik } from 'formik';
import React, {
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';
import { ErrorContext } from '../../ErrorContext';
import { SignInContext } from '../../SignInContext';
import { ToolTip } from 'src/commons/components';
import { AddressTextInput } from 'src/commons/components/AddressTextInput';
import { ApplyHeading } from 'src/commons/components/Apply/Heading';
import Card from 'src/commons/components/Card';
import { CheckBox } from 'src/commons/components/CheckBox';
import { CompletedApplicationModalControl } from 'src/commons/components/CompletedApplicationModalControl';
import { CompletedApplicationModalVariant } from 'src/commons/components/CompletedApplicationModalVariant';
import { CreditCardInfo } from 'src/commons/components/CreditCardInfo';
import { CurrencyTextInput } from 'src/commons/components/CurrencyTextInput';
import { DateTextInput } from 'src/commons/components/DateTextInput';
import { DropDown } from 'src/commons/components/DropDown';
import FileDownloadLink from 'src/commons/components/FileDownloadLink';
import { scrollToErrors } from 'src/commons/components/FocusFormikErrors';
import { Footer } from 'src/commons/components/Footer';
import { MultiSelectInput } from 'src/commons/components/MultiSelectInput';
import { Nav } from 'src/commons/components/Nav/Nav';
import {
  OfferCodeModal,
  OfferCodePrepopFields,
} from 'src/commons/components/OfferCodeModal';
import { OptionalTextInput } from 'src/commons/components/OptionalTextInput';
import { Page } from 'src/commons/components/Page';
import { PhoneNumberInput } from 'src/commons/components/PhoneNumberInput';
import { SchumerBoxContainer } from 'src/commons/components/Schumer';
import { SignInModalControl } from 'src/commons/components/SignInModalControl';
import { SignInModalVariant } from 'src/commons/components/SignInModalVariant';
import { SsnTextInput } from 'src/commons/components/SsnTextInput';
import { TcpaModal } from 'src/commons/components/TcpaModal';
import { TextInput } from 'src/commons/components/TextInput';
import { ZipCodeTextInput } from 'src/commons/components/ZipCodeTextInput';
import {
  ApplicationStatus,
  APPLY_FLOW_IDENTITY_EMAIL,
  CARDSTAR_OVERRIDE_ACCOUNT_CREATION,
  CreateApplicationPayload,
  DeviceType,
  FunnelType,
  GREY_OUT_FN_LN_CRC,
  INCOME_SOURCE_MAP,
  INCOME_SOURCE_TYPES,
  LANDING_PAGE_OFFER_CODE_KEY,
  LegalDocumentContexts,
  LegalDocuments,
  NPC_FUNNEL_DSA_ONE_KEY,
  NPC_FUNNEL_TCPA,
  NPC_UDPATE_EXISTING_PROSPER,
  PROSPER_DM_OFFER_CODE_CAMPAIGN_CODE,
  STATES,
  UNAUTHORIZED_MESSAGE,
} from 'src/commons/constants';
import {
  CreditCardLegalDocument,
  GraphQLResponse,
} from 'src/commons/data/graphql';
import {
  cardStarGetCreditCardAccountId,
  coreDashboardGetCreditCardAccount,
  createCreditCardApplication,
  getCreditCardApplications,
  getCreditCardApplicationsByUser,
  getCreditCardLegalDocuments,
  getEmailExistenceQuery,
  getLoggedInUser,
  updateUserMutation,
} from 'src/commons/data/queries';
import { createUser } from 'src/commons/data/web-auth';
import {
  getMaxDateOfBirthByMinAge,
  validateDate,
  validateMaxAgeOrYounger,
  validateMinAgeOrOlder,
} from 'src/commons/utilities/dateValidation';
import {
  getFeatureVariables,
  getOptimizelyVariation,
  ifFeatureFlagOn,
} from 'src/commons/utilities/feature-flags';
import {
  ADDRESS_REGEX,
  AVAILABLE_STATES,
  EMAIL_REGEX,
  finalizeCurrency,
  finalizeDateOfBirth,
  finalizeOptionalCurrency,
  formatCurrencyInputWithCommas,
  isValidPhoneNumber,
  MAX_REGEX,
  NAME_REGEX,
  NOT_PO_BOX_REGEX,
  SSN_REGEX,
} from 'src/commons/utilities/formatting';
import { getUserIdentityFromSessionStorage } from 'src/commons/utilities/identity';
import {
  getJWTFromLocalStorage,
  setJWTInLocalStorage,
} from 'src/commons/utilities/jwt';
import { getStateFromZip, validateZip } from 'src/commons/utilities/location';
import {
  heapAddUserProperties,
  heapIdentify,
  heapTrackClientError,
  heapTrackElementRendered,
  logError,
  nrAddPageProperty,
  nrIdentify,
  pushToDataLayer,
} from 'src/commons/utilities/logging';
import {
  getCampaignRefCode,
  sendMarketingTouch,
} from 'src/commons/utilities/marketingTouch';
import { parseQueryString } from 'src/commons/utilities/queryServices';
import { prefixRoute } from 'src/commons/utilities/routes';
import { trackChangeOnPrepop } from 'src/commons/utilities/syntheticEventTracking';
import { useExitPrompt } from 'src/commons/utilities/useExitPrompt';
import { isMobileUserAgent } from 'src/commons/utilities/userAgent';

import {
  AdditionalIncomeHintContents,
  ApplicationDSA,
  ApplicationError,
  ApplicationIdentity,
  ApplicationLoader,
  ESignCheckboxCopy,
  YearlyIncomeHintContents,
} from './components';
import {
  AboutYouWrapper,
  ApplyHeadingWrapper,
  Body,
  CheckBoxText,
  Form,
  FormSection,
  FormSectionHeading,
  FullWidthRow,
  HalfWidthInput,
  HalfWidthInputsContainer,
  IncomeSectionHeading,
  Main,
  SchumerNotice,
  SchumerSection,
  SubmitButton,
  SubmitText,
  TotalIncomeLabel,
  TotalIncomeRow,
  TotalIncomeValue,
} from './styles';
import { DsaVariantType, ValidateUserProps } from './types';

// Apply
// Single-page registration form for Credit Card

export const Apply = () => {
  // Constants ------------------------------------------------------------------------------------
  const SCHUMER_NOTICE_COPY =
    'These documents contain important information, including your ' +
    'consent to doing business electronically.';

  // State ----------------------------------------------------------------------------------------
  const [loading, setLoading] = useState(true); // loading spinner
  const [height, setHeight] = useState(window?.innerHeight); // screen height
  const [checkingForExistingUser, setCheckingForExistingUser] = useState(false);
  const [disableEmail, setDisableEmail] = useState(false);
  const [disableFirstName, setDisableFirstName] = useState(false);
  const [disableLastName, setDisableLastName] = useState(false);
  const [disableDOB, setDisableDOB] = useState(false);
  const [disableSSN, setDisableSSN] = useState(false);
  const [legalDocuments, setLegalDocuments] = useState([]); // set upon successful doc request
  const [applicationId, setApplicationId] = useState(''); // set upon successful app creation
  // for errors that happen before user starts application
  const [showFailure, setShowFailure] = useState(false);
  // for showing identity DOB screen before user continue filling the application
  const [showDOBIdentity, setShowDOBIdentity] = useState(false);
  // for showing identity DOB and SSN screen before user continue filling the application
  const [showDOBAndSSNIdentity, setShowDOBAndSSNIdentity] = useState(false);
  // for showing identity Error (Can't prefill the application)
  const [showIdentityError, setShowIdentityError] = useState(false);
  // for setting loggedIn user info to compare with Identity screen
  const [loggedInUser, setLoggedInUser] = useState<{
    dateOfBirth: string | null;
    ssn: string | null;
  }>({
    dateOfBirth: null,
    ssn: null,
  });
  // show completed application modal if email is linked to an already completed applicant
  const [showCompletedApplicationModal, setShowCompletedApplicationModal] =
    useState(false);
  const [showSignInEmailOnBlurModal, setShowSignInEmailOnBlurModal] =
    useState(false);
  // toggle offer code modal if they have the offer code query param
  const [showOfferCodeModal, setShowOfferCodeModal] = useState<boolean>(false);
  // show/hide additional income prompt and input
  const [showAdditionalIncome, setShowAdditionalIncome] =
    useState<boolean>(true);
  // assign landing page offer code to trigger auto submit in OfferCodeModal
  const [passedOfferCode, setPassedOfferCode] = useState('');
  // control the offer code modal to remain hidden
  const [forceHideModal, setForceHideModal] = useState(false);

  // tcpa modal state and FF
  const isNewTcpaEnabled = ifFeatureFlagOn(NPC_FUNNEL_TCPA);
  const [showTcpaModal, setShowTcpaModal] = useState(false);
  // Accepted by default
  const [acceptedTcpa, setAcceptedTcpa] = useState(true);

  // control the migration status of the user

  const [migrationNeeded, setMigrationNeeded] = useState(false);
  let creditCardAccountId = '';

  // declare AbortController constant here
  let abortController: AbortController;

  const isFFidentityEmailOn = ifFeatureFlagOn(APPLY_FLOW_IDENTITY_EMAIL);
  const isCardStarRedirectOn = ifFeatureFlagOn(
    CARDSTAR_OVERRIDE_ACCOUNT_CREATION
  );
  const dsaFFVariant = getOptimizelyVariation(
    NPC_FUNNEL_DSA_ONE_KEY
  ).toLowerCase() as DsaVariantType;
  const CompletedApplicationModal = isCardStarRedirectOn
    ? CompletedApplicationModalVariant
    : CompletedApplicationModalControl;
  const SignInModal = isCardStarRedirectOn
    ? SignInModalVariant
    : SignInModalControl;
  const isAllowUpdateExistingProsperUser = ifFeatureFlagOn(
    NPC_UDPATE_EXISTING_PROSPER
  );

  const prepopLoggedInUserFields: { [key: string]: string } = {};
  // Context --------------------------------------------------------------------------------------
  const { setShowErrorModal } = useContext(ErrorContext);
  const {
    userEmail,
    setUserEmail,
    setExternalUserId,
    setShowSignInModal,
    externalUserId,
    setFirstName,
    setLastName,
    setDateOfBirth,
  } = useContext(SignInContext);

  // Hooks ----------------------------------------------------------------------------------------
  const navigate = useNavigate();

  // Refs for each input field (so we can scroll to errors)
  const inputRefs: { [keys: string]: MutableRefObject<any> } = {
    firstName: useRef(null),
    lastName: useRef(null),
    dateOfBirth: useRef(null),
    ssn: useRef(null),
    street1: useRef(null),
    street2: useRef(null),
    city: useRef(null),
    state: useRef(null),
    zip: useRef(null),
    incomeSourceTypes: useRef(null),
    annualIncome: useRef(null),
    additionalIncome: useRef(null),
    housingPayment: useRef(null),
    email: useRef(null),
    confirmEmail: useRef(null),
    phoneNumber: useRef(null),
    hasConsentedToTcpa: useRef(null),
    eSign: useRef(null),
    isDsaEnrolled: useRef(null),
  };
  // Hold on to any successfully used offer codes, to use when creating the app
  const offerCode = useRef<string | null>(null);
  // Track prepop when logged in before page loads (needed since form is not rendered yet)
  const prepopOnLoadRef = useRef<{ [key: string]: string } | null>(null);
  // Ref for the submit ("See my offer") button at the bottom of the page
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);

  let ssnValidation;
  const isGreyOutFNAndLNCrcOn = ifFeatureFlagOn(GREY_OUT_FN_LN_CRC);
  const { crcCampaigns } = getFeatureVariables(GREY_OUT_FN_LN_CRC) || '';

  const getCRCValue = (): string => {
    const queryString = decodeURIComponent(window.location.search);
    const queryMap = parseQueryString(queryString);
    const crcValue = queryMap.get('crc')?.trim();
    return crcValue || '';
  };

  if (isFFidentityEmailOn && disableSSN) {
    ssnValidation = Yup.string().notRequired();
  } else {
    ssnValidation = Yup.string()
      .trim()
      .min(9, 'Enter a valid 9-digit SSN')
      .max(9, 'Enter a valid 9-digit SSN')
      .matches(SSN_REGEX, 'Enter a valid SSN')
      .required('Enter your SSN');
  }

  const submitCreateCreditCardApplication = async (
    payload: CreateApplicationPayload
  ) => {
    // try to create the application
    const res = await createCreditCardApplication(payload);
    // if we didn't get an application response then bail
    if (!(res && res.data && res.data.createCreditCardApplication)) {
      throw new Error('Failed to create an application');
    }
    // otherwise send a marketing touch and return the application
    const {
      applicationId: externalApplicationId,
      applicationAttribution: { offerCode: applicationOfferCode },
    } = res.data.createCreditCardApplication;
    const queryString = decodeURIComponent(window.location.search);
    const deviceType = isMobileUserAgent()
      ? DeviceType.MOBILE_WEB
      : DeviceType.DESKTOP;
    sendMarketingTouch(
      queryString,
      deviceType,
      externalApplicationId,
      applicationOfferCode
    );
    return res.data.createCreditCardApplication;
  };

  // Formik
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      ssn: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip: '',
      incomeSourceTypes: new Array<string>(),
      annualIncome: '',
      additionalIncome: '',
      housingPayment: '',
      email: '',
      confirmEmail: '',
      phoneNumber: '',
      hasConsentedToTcpa: true,
      eSign: false,
      isDsaEnrolled: undefined,
    },
    initialStatus: {
      showConfirmEmail: false,
      emailHasFocus: false,
      checkMatchingZipAndState: false,
    },
    validationSchema: Yup.object().shape({
      // fields with values affecting validations of other fields are placed at the top
      state: Yup.string() // state affects the validation of dateOfBirth & zip
        .max(
          2,
          'Select a valid 2-character abbreviation of your state from the options provided'
        )
        .required('Select your state')
        .test('is-available-state', (value, ctx) => {
          // Allow Maryland if a valid offer code was applied
          const STATES_MARYLAND_ALLOWED = /^((?!IA)(?!WV).)*$/;
          const isValidMarylandOfferCode =
            offerCode.current &&
            (offerCode.current.startsWith('GD') ||
              offerCode.current.startsWith('GE') ||
              offerCode.current.startsWith('GH'));
          // Check validity of offer code and for appropriate prefix
          if (offerCode.current && isValidMarylandOfferCode) {
            if (!STATES_MARYLAND_ALLOWED.test(value)) {
              return ctx.createError({
                message: `We are not able to offer credit cards to residents of ${value} at this time`,
              });
            }
            // Pass validation allowing Maryland when a valid offer code was applied
            return true;
          }
          // Test input value against valid states determined by FF npc-apply-maryland
          const validStates = ifFeatureFlagOn('npc-apply-maryland')
            ? STATES_MARYLAND_ALLOWED
            : AVAILABLE_STATES;
          if (!validStates.test(value)) {
            return ctx.createError({
              message: `We are not able to offer credit cards to residents of ${value} at this time`,
            });
          }
          return true;
        }),
      // about-you
      firstName: Yup.string()
        .trim()
        .max(40, 'Enter a first name with no more than 40 characters')
        .required('Enter your first name'),
      lastName: Yup.string()
        .trim()
        .max(40, 'Enter a last name with no more than 40 characters')
        .required('Enter your last name'),
      dateOfBirth: Yup.string()
        .required('Enter your date of birth')
        .test('is-valid-date', 'Enter a valid date', (value) =>
          validateDate(value)
        )
        .test(
          'is-user-born-after-1900',
          'Enter a valid date of birth',
          (value) => validateMaxAgeOrYounger(value)
        )
        .test(
          'is-default-user-the-minimum-age-18-or-older',
          'Thanks for your interest, but you must be at least 18 years old to apply for a credit card through Prosper.',
          (value) => validateMinAgeOrOlder(18, value)
        ),
      email: Yup.string()
        .required('Enter your email address')
        .max(50, 'Enter an email address with no more than 50 characters')
        .matches(EMAIL_REGEX, 'Enter a valid email address'),
      confirmEmail: Yup.string()
        .required('Confirm your email address')
        .max(50, 'Enter an email address with no more than 50 characters')
        .matches(EMAIL_REGEX, 'Enter a valid email address')
        .oneOf([Yup.ref('email'), null], 'Email addresses must match'),
      ssn: ssnValidation,
      // your-address
      street1: Yup.string()
        .trim()
        .min(1, 'Enter a valid street address')
        .max(50, 'Enter a street address with no more than 50 characters')
        .matches(
          NOT_PO_BOX_REGEX,
          'Enter a street address, P.O. Boxes are not accepted'
        )
        .required('Enter your street address'),
      street2: Yup.string()
        .trim()
        .max(50, 'Enter a street address with no more than 50 characters')
        .matches(
          NOT_PO_BOX_REGEX,
          'Enter a street address, P.O. Boxes are not accepted'
        ),
      city: Yup.string()
        .trim()
        .min(1, 'Enter a valid city')
        .max(50, 'Enter a city with no more than 50 characters')
        .required('Enter your city'),
      zip: Yup.string().when('state', {
        is: (state: string) => state !== undefined,
        then: () =>
          Yup.string()
            .trim()
            .required('Enter your zip code')
            .test(
              // Check if zip input is valid format
              'is-valid-zip',
              'Enter a valid zip code',
              (value) => !!validateZip(value)
            )
            .test(
              // Check if zip input matches state
              'is-valid-zip-for-state',
              'Zip code does not match the state',
              (value) => isValidZipForState(value)
            ),
        otherwise: () =>
          Yup.string()
            .trim()
            .required('Enter your zip code')
            .test(
              'is-valid-zip',
              'Enter a valid zip code',
              (value) => !!validateZip(value)
            ),
      }),
      housingPayment: Yup.string()
        .trim()
        .required('Enter your monthly housing payment')
        .max(6, 'Enter a housing payment no greater than $99,999')
        .matches(MAX_REGEX, 'Enter a housing payment no greater than $99,999'),

      // income-information
      incomeSourceTypes: Yup.array()
        .of(Yup.string())
        .min(1, 'Select your income type(s)')
        .required('Select your income type(s)'),
      annualIncome: Yup.string()
        .trim()
        .required('Enter your annual income')
        .max(9, 'Enter an annual income no greater than $9,999,999')
        .matches(
          MAX_REGEX,
          'Enter an annual income no greater than $9,999,999'
        ),
      additionalIncome: Yup.string()
        .trim()
        .max(9, 'Enter an additional income no greater than $9,999,999')
        .matches(
          MAX_REGEX,
          'Enter an additional income no greater than $9,999,999'
        ),

      // contact-information
      phoneNumber: Yup.string()
        .trim()
        .required('Enter your phone number')
        .min(12, 'Enter a valid phone number')
        .test('is-valid-phone-number', 'Enter a valid phone number', (value) =>
          isValidPhoneNumber(value)
        ),
      hasConsentedToTcpa: Yup.boolean().required(),
      eSign: Yup.boolean().oneOf(
        [true],
        'Please review and agree to the documents above'
      ),
      isDsaEnrolled: Yup.boolean().required('Make a selection to continue'),
    }),
    onSubmit: async (values) => {
      // Scroll to the top of the page so the LoadingCard is shown upon successful submission
      window.scrollTo(0, 0);
      try {
        // grab all the values from the form
        const {
          firstName,
          lastName,
          dateOfBirth,
          ssn,
          phoneNumber,
          email,
          street1,
          street2,
          city,
          state,
          zip,
          annualIncome,
          additionalIncome,
          housingPayment,
          incomeSourceTypes,
          hasConsentedToTcpa,
          isDsaEnrolled,
        } = values;

        let userId; // for tracking purposes
        let userType; // for Google Analytics 4 tagging

        // Returns list of documents client has consented to onSubmit
        const getConsentedDocuments = () =>
          legalDocuments
            .filter((document: CreditCardLegalDocument) => {
              if (document.name === LegalDocuments.TCPA_AUTHORIZATION) {
                // If tcpa then only add if it's been agreed to
                return isNewTcpaEnabled ? acceptedTcpa : hasConsentedToTcpa;
              }
              if (document.name === LegalDocuments.TERMS_AND_CONDITIONS) {
                return false;
              }
              // All other agreements must be agreed to so add them
              return true;
            })
            .filter(
              (document: CreditCardLegalDocument) =>
                LegalDocuments.TCPA_AUTHORIZATION_HTML !== document.name
            )
            // Map and pull only name and version for each document to send to BE
            .map((document: CreditCardLegalDocument) => {
              const { name, version } = document;
              return { name, version };
            });

        // if there isn't a jwt in local storage already
        if (!getJWTFromLocalStorage()) {
          // For user password a random GUID will be used.
          // Currently only PL users can be "passwordless",
          // We have a tech debt ticket to fix this: https://jira.prosper.com/browse/NPC-2930.
          // Once this is in place, we need to allow users to be created without passwords,
          // and "log them in" to get a valid user token for the JWT.
          const newUserPassword = uuidv4(); // capture so we can "log in" new user below
          const fullStreetAddress = street2
            ? `${street1.trim()},${street2.trim()}`
            : street1.trim();

          // make a user from inputted values
          const userPayload = {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            dateOfBirth: dateOfBirth.trim(),
            email: email.trim(),
            password: newUserPassword,
            phoneNumber: phoneNumber.trim(),
            annualIncome: finalizeCurrency(annualIncome.trim()),
            additionalIncome: showAdditionalIncome
              ? finalizeOptionalCurrency(additionalIncome.trim())
              : null,
            tcpaConsent: isNewTcpaEnabled ? acceptedTcpa : hasConsentedToTcpa,
            eConsent: true,
            webBankPrivacyPolicyConsent: false,
            address: {
              streetAddress: fullStreetAddress,
              city: city.trim(),
              state: state.trim(),
              zip: zip.trim(),
              stateOfResidence: state,
            },
            employmentStatus: INCOME_SOURCE_MAP.get(incomeSourceTypes[0]),
            isDsaEnrolled,
          };

          // Check if the email already exists
          const getEmailExistenceResponse = await getEmailExistenceQuery(email);
          const {
            data: { getEmailExistence },
          } = getEmailExistenceResponse;

          if (getEmailExistence && getEmailExistence.exists) {
            throw new Error('User already exists');
          }
          // Create new user
          const newUser = await createUser(userPayload);
          const {
            external_user_id: newExternalUserId,
            token: prosperJWT,
            user,
          } = newUser;
          const {
            roles,
            personal_info: personalInfo,
            user_id: newUserId,
          } = user;
          if (!prosperJWT) {
            throw new Error('Failed to log in new user');
          }
          setJWTInLocalStorage(prosperJWT);
          const userData = {
            externalUserId: newExternalUserId,
            firstName: personalInfo.first_name,
            roles,
            userId: newUserId,
          };
          localStorage.setItem('user-info', JSON.stringify(userData));
          if (newExternalUserId) {
            setExternalUserId(newExternalUserId);
            heapIdentify(newExternalUserId);
            nrIdentify(newExternalUserId);
            pushToDataLayer({ user_id: newExternalUserId });
            userId = newExternalUserId;
            userType = 'new';
          }
        } else {
          // Get externalUserId from SignInContext for tracking purposes
          userId = externalUserId;
          userType = 'existing';
          if (isAllowUpdateExistingProsperUser) {
            const fullStreetAddress = street2
              ? `${street1.trim()},${street2.trim()}`
              : street1.trim();

            // make a user from inputted values
            const userPayload = {
              phoneNumber: phoneNumber.trim(),
              tcpaConsent: isNewTcpaEnabled ? acceptedTcpa : hasConsentedToTcpa,
              eConsent: true,
              webBankPrivacyPolicyConsent: false,
              address: {
                streetAddress: fullStreetAddress,
                city: city.trim(),
                state: state.trim(),
                zip: zip.trim(),
                stateOfResidence: state,
              },
            };

            // update user (CardStar Migration phone and address mismatch)
            /*
              PL user with listingstatus in of this
              ['ACTIVE', 'On Hold', 'PENDING_ACTIVATION', 'PENDING_COMPLETION', 'ONHOLD']
              We continue to create credit card application for these users and
              update their phone and address at the migration time.
            */
            await updateUserMutation(userPayload);
          }
        }

        // Push user_type to dataLayer for Google Analytics 4 tagging
        if (userType) pushToDataLayer({ user_type: userType });

        const campaignRefCode = getCampaignRefCode(
          decodeURIComponent(window.location.search)
        );

        const deviceType = isMobileUserAgent()
          ? DeviceType.MOBILE_WEB
          : DeviceType.DESKTOP;

        const funnelType = ((): FunnelType => {
          if (dsaFFVariant === 'variant-a')
            return FunnelType.LONG_FORM_DSA_DIRECT;
          if (dsaFFVariant === 'variant-b')
            return FunnelType.LONG_FORM_DSA_INDIRECT;
          return FunnelType.LONG_FORM;
        })();

        // now try to make an application
        const createApplicationPayload: CreateApplicationPayload = {
          applicant: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            dateOfBirth: finalizeDateOfBirth(dateOfBirth.trim()),
            phoneNumber: phoneNumber.trim(),
            email: email.trim(),
            mailingAddress: {
              street1: street1.trim(),
              street2: street2.trim(),
              city: city.trim(),
              state,
              zip: zip.trim(),
            },
            income: {
              incomeSourceTypes,
              annualIncome: finalizeCurrency(annualIncome.trim()),
              additionalIncome: showAdditionalIncome
                ? finalizeOptionalCurrency(additionalIncome.trim())
                : null,
              housingPayment: finalizeCurrency(housingPayment.trim()),
            },
          },
          legalDocumentsToSave: getConsentedDocuments(),
          campaignRefCode,
          deviceType,
          offerCode: offerCode.current,
          funnelType,
          isDsaEnrolled,
        };
        // mask ssn has xxx-xx-4digits
        if (!(isFFidentityEmailOn && ssn.trim().includes('xxxxx'))) {
          createApplicationPayload.applicant.ssn = ssn.trim();
        }

        const response = await submitCreateCreditCardApplication(
          createApplicationPayload
        );
        // cache app ID for polling status if app was created successfully
        setApplicationId(response.applicationId);
        heapAddUserProperties({
          creditCardApplicationId: `${response.applicationId}`,
          creditCardApplicationLatestCRC: campaignRefCode,
        });
        nrIdentify(userId);
        nrAddPageProperty('externalApplicationId', response.applicationId);
        nrAddPageProperty('applicationAttributionCrc', campaignRefCode);
      } catch (error: any) {
        // if we tried to create a user for an email that already exists
        if (error.message === 'User already exists') {
          // set the email of the existing user in context and open the SignInModal
          setUserEmail(values.email);
          setShowSignInModal(true);
          // Track SignInModal visibility
          heapTrackElementRendered('SignInModal');
        } else if (error.message !== UNAUTHORIZED_MESSAGE) {
          // otherwise just signal error
          setShowErrorModal(true);
          logError(error, {
            errorName: error.name || 'PageError',
            errorMessage: error.message || 'Unexpected error occurred',
            component: 'Apply',
            function: 'onSubmit',
          });
        }
      }
    },
  });

  // Get legal documents for CheckBox Component
  const fetchLegalDocuments = async () => {
    setLoading(true);
    try {
      const response: GraphQLResponse = await getCreditCardLegalDocuments(
        LegalDocumentContexts.DIRECT_APP_REGISTRATION
      );
      const { data, errors } = response;

      if (
        data &&
        data.getCreditCardLegalDocuments &&
        !abortController.signal.aborted
      ) {
        setLegalDocuments(data.getCreditCardLegalDocuments);
      }
      if (errors) {
        setShowFailure(true);
        const errorMessage =
          errors && errors[0] && errors[0].code
            ? errors[0].code
            : 'Unexpected error occurred fetching LegalDocumentContexts.DIRECT_APP_REGISTRATION,';
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      if (
        !abortController.signal.aborted &&
        err.message !== UNAUTHORIZED_MESSAGE
      ) {
        setShowFailure(true);
      }
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'Apply',
          function: 'fetchLegalDocuments',
        });
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  // prompt the user to confirm if they leave the page
  // only if the form has been interacted with
  useExitPrompt(
    'Are you sure you want to leave?',
    formik.dirty || formik.values.incomeSourceTypes.length > 0,
    undefined,
    undefined
  );

  // Functions ------------------------------------------------------------------------------------

  // Clear values for email and confirmEmail input fields
  const clearEmailFields = () => {
    formik.values.email = '';
    formik.values.confirmEmail = '';
    formik.validateField('email');
    formik.validateField('confirmEmail');
    // Redirect focus back to email input after clearing existing values
    inputRefs.email.current?.focus();
  };

  const validatingUserWithSessionStorageInfo = ({
    firstName,
    lastName,
    dateOfBirth,
    ssn,
  }: ValidateUserProps) => {
    const {
      firstName: firstNameFromSession,
      lastName: lastNameFromSession,
      dateOfBirth: dateOfBirthFromSession,
    } = getUserIdentityFromSessionStorage();
    const isMatch =
      firstNameFromSession &&
      lastNameFromSession &&
      dateOfBirthFromSession &&
      firstName &&
      lastName &&
      dateOfBirth &&
      firstNameFromSession.toLowerCase() === firstName.toLowerCase() &&
      lastNameFromSession.toLowerCase() === lastName.toLowerCase() &&
      dateOfBirth === dateOfBirthFromSession;
    if (!isMatch) {
      setLoggedInUser({
        dateOfBirth,
        ssn,
      });
    }
    return {
      // when getLoggedUser API has no ssn in their account.
      isDisableSSN: !!ssn,
      isMatch,
    };
  };

  const setMigrationStatus = async () => {
    try {
      const {
        data: {
          coreDashboardGetCreditCardAccount: { accountMigrationStatus },
        },
      } = await coreDashboardGetCreditCardAccount(creditCardAccountId);
      setMigrationNeeded(
        accountMigrationStatus !== 'ACCOUNT_MIGRATION_COMPLETED' &&
          accountMigrationStatus !== 'ACCOUNT_MIGRATION_NOT_DETERMINED' &&
          accountMigrationStatus !== 'ACCOUNT_MIGRATION_NOT_NEEDED'
      );
    } catch (err: any) {
      logError(err, {
        errorName: err.name || 'PageError',
        errorMessage: err.message || 'Unexpected error occurred',
        component: 'Apply',
        function: 'setMigrationStatus',
      });
    }
  };

  /**
   * This fn is called if there is a JWT present in local storage.
   * 1. Check for both in-progress or completed applications.
   * If JWT is invalid (401), do NOT perform loginRedirect.
   * 2. If User has in-progress or a completed application,
   * initiate app-resume logic w/ most recent application.
   * 3. If user has no applications & no 401/errors occur from ^,
   * JWT must be valid so fetch logged in user by JWT & pre-pop form data.
   * If a 401/errors occur from querying for applications,
   * JWT is removed and user can proceed w/ registration.
   * This page is also where in-progress users are redirected during Plaid OAuth flow.
   * oauth_state_id qs param indicates this
   */
  const checkForApplicationsByLoggedInUser = async () => {
    setCheckingForExistingUser(true);
    try {
      // CardStar popup if user has a credit card account
      if (isCardStarRedirectOn) {
        if (!creditCardAccountId) {
          const {
            data: { cardStarGetCreditCardAccountId: creditCardAccountIds },
          } = await cardStarGetCreditCardAccountId();
          if (
            creditCardAccountIds &&
            creditCardAccountIds.creditCardAccountIds.length > 0
          ) {
            [creditCardAccountId] = creditCardAccountIds.creditCardAccountIds;
          }
        }
        if (creditCardAccountId) {
          setShowCompletedApplicationModal(true);
          const res = await getLoggedInUser();
          if (!res.errors && res.data && res.data.getLoggedInUser) {
            const {
              externalUserId: userId,
              firstName,
              lastName,
              email,
              dateOfBirth,
              ssn,
            } = res.data.getLoggedInUser;

            if (firstName) {
              formik.setFieldValue('firstName', firstName);
              if (isFFidentityEmailOn) setDisableFirstName(true);
              prepopLoggedInUserFields.firstName = firstName;
            }
            if (lastName) {
              formik.setFieldValue('lastName', lastName);
              if (isFFidentityEmailOn) setDisableLastName(true);
              prepopLoggedInUserFields.lastName = lastName;
            }
            if (dateOfBirth) {
              formik.setFieldValue('dateOfBirth', dateOfBirth);
              if (isFFidentityEmailOn) setDisableDOB(true);
              prepopLoggedInUserFields.dateOfBirth = dateOfBirth;
            }
            if (email) {
              formik.setFieldValue('email', email);
              formik.setFieldValue('confirmEmail', email);
              setDisableEmail(true);
              prepopLoggedInUserFields.email = email;
              prepopLoggedInUserFields.confirmEmail = email;
            }
            if (isFFidentityEmailOn && ssn) {
              const formatSSN = ssn.replace(/-/g, '');
              formik.setFieldValue('ssn', formatSSN);
              setDisableSSN(true);
              prepopLoggedInUserFields.ssn = formatSSN;
            }

            setExternalUserId(userId);
            heapIdentify(userId);
            nrIdentify(userId);
            prepopOnLoadRef.current = prepopLoggedInUserFields;
            await setMigrationStatus();
          }
          return;
        }
      }
      // Fetch applications by user in JWT
      // We are looking for in-progress applications (isTerminal: false) or APPLICATION_COMPLETED
      const queryFields = {
        limit: 1,
        status: ['APPLICATION_COMPLETED'],
        isTerminal: false,
      };
      // Try to fetch applications belonging to the JWT'd user
      // Don't redirect to login on 401,
      // JWT will be removed form local storage and user can proceed w/ registration.
      const response = await getCreditCardApplicationsByUser(
        queryFields,
        false
      );
      const { data, errors } = response;
      if (
        !errors &&
        data &&
        data.getCreditCardApplicationsByUser &&
        !abortController.signal.aborted
      ) {
        // we got a response meaning no 401 or other errors occurred, so JWT must be valid still.
        // if no in-progress/completed apps for the user, fetch user & pre-pop form data.
        // existing prosper user (non cc application)
        if (data.getCreditCardApplicationsByUser.data.length === 0) {
          const res = await getLoggedInUser();
          if (isFFidentityEmailOn && getUserIdentityFromSessionStorage()) {
            const { isVerified } = getUserIdentityFromSessionStorage();
            if (!isVerified) {
              const { isDisableSSN, isMatch } =
                validatingUserWithSessionStorageInfo(res.data.getLoggedInUser);
              if (!isMatch) {
                if (isDisableSSN) {
                  // show modal asking user to enter dob and ssn
                  setShowDOBAndSSNIdentity(true);
                } else {
                  // show modal asking user to enter dob
                  setShowDOBIdentity(true);
                }
              }
            }
          }
          if (!res.errors && res.data && res.data.getLoggedInUser) {
            const {
              externalUserId: userId,
              firstName,
              lastName,
              email,
              dateOfBirth,
              ssn,
            } = res.data.getLoggedInUser;

            if (firstName) {
              formik.setFieldValue('firstName', firstName);
              if (isFFidentityEmailOn) setDisableFirstName(true);
              prepopLoggedInUserFields.firstName = firstName;
            }
            if (lastName) {
              formik.setFieldValue('lastName', lastName);
              if (isFFidentityEmailOn) setDisableLastName(true);
              prepopLoggedInUserFields.lastName = lastName;
            }
            if (dateOfBirth) {
              formik.setFieldValue('dateOfBirth', dateOfBirth);
              if (isFFidentityEmailOn) setDisableDOB(true);
              prepopLoggedInUserFields.dateOfBirth = dateOfBirth;
            }
            if (email) {
              formik.setFieldValue('email', email);
              formik.setFieldValue('confirmEmail', email);
              setDisableEmail(true);
              prepopLoggedInUserFields.email = email;
              prepopLoggedInUserFields.confirmEmail = email;
            }
            if (isFFidentityEmailOn && ssn) {
              const formatSSN = ssn.replace(/-/g, '');
              formik.setFieldValue('ssn', formatSSN);
              setDisableSSN(true);
              prepopLoggedInUserFields.ssn = formatSSN;
            }

            setExternalUserId(userId);
            heapIdentify(userId);
            nrIdentify(userId);
            prepopOnLoadRef.current = prepopLoggedInUserFields;
          }
        } else {
          // Grab the user's most recent application from the array
          const application = data.getCreditCardApplicationsByUser.data[0];
          heapIdentify(application.externalUserId);
          nrIdentify(application.externalUserId);
          heapAddUserProperties({
            creditCardApplicationId: `${application.applicationId}`,
          });
          nrAddPageProperty('externalApplicationId', application.applicationId);
          nrAddPageProperty(
            'applicationAttributionCrc',
            application.applicationAttribution?.campaignRefCode
          );
          // Check the query string in case we are in the midst of Plaid Link OAuth
          const queryString = decodeURIComponent(window.location.search);
          const searchParams = new URLSearchParams(queryString);
          // oauth_state_id is added as a query param by Plaid during OAuth bank connection
          if (searchParams.has('oauth_state_id')) {
            const oauthStateId = searchParams.get('oauth_state_id');
            // Depending on status, redirect to the page from which Plaid was launched
            if (
              application.applicationStatus ===
              ApplicationStatus.PENDING_UNCONDITIONAL_SUBMISSION
            ) {
              const resumeBankConnectPath = `/application/${application.applicationId}/auto-pay/setup?oauth_state_id=${oauthStateId}`;
              // Redirect to the AutoPay page with the oauth_state_id query param
              navigate(prefixRoute(resumeBankConnectPath));
            } else {
              const resumeBankConnectPath = `/application/${application.applicationId}/connect-to-bank?oauth_state_id=${oauthStateId}`;
              // Redirect to the ConnectToBank page with the oauth_state_id query param
              navigate(prefixRoute(resumeBankConnectPath));
            }
          } else {
            // Redirect signed in user to their place in the application
            navigate(prefixRoute(`/application/${application.applicationId}`));
          }
        }
      }
    } catch (err: any) {
      if (
        !abortController.signal.aborted &&
        err.message !== UNAUTHORIZED_MESSAGE
      ) {
        setShowFailure(true);
      }
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'Apply',
          function: 'checkForApplicationsByLoggedInUser',
        });
      }
    } finally {
      if (!abortController.signal.aborted) {
        setCheckingForExistingUser(false);
      }
    }
  };

  const checkEmailExistence = async (email: string) => {
    try {
      // Check email existence and show the sign in modal if necessary
      const emailExistenceResponse = await getEmailExistenceQuery(email);
      const { data } = emailExistenceResponse;
      if (data && data.getEmailExistence && data.getEmailExistence.exists) {
        const queryFields = {
          limit: 1,
          status: ['APPLICATION_COMPLETED'],
          email,
        };
        const creditCardApplicationsRes =
          await getCreditCardApplications(queryFields);
        if (
          creditCardApplicationsRes.data &&
          creditCardApplicationsRes.data.getCreditCardApplications &&
          creditCardApplicationsRes.data.getCreditCardApplications.data &&
          creditCardApplicationsRes.data.getCreditCardApplications.data.length
        ) {
          // Show existing credit card modal
          if (isCardStarRedirectOn) {
            setShowSignInEmailOnBlurModal(true);
          } else {
            setShowCompletedApplicationModal(true);
          }
          // Track CompletedApplicationSignInModal visibility
          heapTrackElementRendered('CompletedApplicationSignInModal');
        } else {
          // Set the user's email in the context
          setFirstName(formik.values.firstName);
          setLastName(formik.values.lastName);
          setDateOfBirth(formik.values.dateOfBirth);
          setUserEmail(email);
        }
      }
    } catch (err: any) {
      // Log error without showing error UI
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'Apply',
          function: 'checkEmailExistence',
        });
      }
    }
  };

  // Returns document url that matches documentName
  const getViewUrl = (documentName: string) => {
    const eSignLegalDocument: CreditCardLegalDocument | undefined =
      legalDocuments.find(
        (document: CreditCardLegalDocument) => document.name === documentName
      );
    if (eSignLegalDocument) {
      const { viewUrl } = eSignLegalDocument;
      return viewUrl;
    }
    setShowFailure(true);
    return '';
  };

  const getDocumentTitle = (documentName: string) => {
    const selectedDocument = legalDocuments.find(
      (document: CreditCardLegalDocument) => document.name === documentName
    );
    //  complains of instances of being undefined
    if (selectedDocument) {
      const { title } = selectedDocument;
      return title;
    }
    setShowFailure(true);
    return '';
  };

  // TODO: submit button be disabled if docs have not been loaded
  const applyTCPACheckboxCopy = () => {
    // Look through legalDocuments and find the TCPA document
    const tcpaLegalDocument: CreditCardLegalDocument | undefined =
      legalDocuments.find(
        (document: CreditCardLegalDocument) =>
          document.name === LegalDocuments.TCPA_AUTHORIZATION
      );
    if (tcpaLegalDocument) {
      const { viewUrl } = tcpaLegalDocument;
      return (
        <CheckBoxText id="tcpa-checkbox-text">
          I would like updates about products or services and agree to
          Prosper&#39;s{' '}
          <FileDownloadLink href={viewUrl} rel="noreferrer" target="_blank">
            {getDocumentTitle(LegalDocuments.TCPA_AUTHORIZATION)}
          </FileDownloadLink>
          . Message &amp; data rates may apply. I understand I can opt out at
          any time.
        </CheckBoxText>
      );
    }
    // TODO: default return should be updated
    setShowFailure(true);
    return null;
  };

  // Compare the state of the given zip code with the current input value for state
  async function isValidZipForState(zip: string): Promise<boolean> {
    // If formik.values.state hasn't been updated yet, return true for now & validate on next cycle
    if (!formik.values.state) return true;
    const { status, errors } = formik;
    // Only call API if checkMatchingZipAndState is true
    if (status.checkMatchingZipAndState) {
      try {
        const stateFromZip = await getStateFromZip(zip);
        // Persist unrelated status variables
        const { showConfirmEmail, emailHasFocus } = status;
        // Set status variable to false after API call to avoid excessive/redundant requests
        formik.setStatus({
          showConfirmEmail,
          emailHasFocus,
          checkMatchingZipAndState: false,
        });
        // Return true when output of getStateFromZip matches the current state input value
        if (stateFromZip && formik.values.state === stateFromZip) {
          return true;
        }
        // Return false when output of getStateFromZip doesn't match formik.values.state
        return false;
      } catch (zipError: any) {
        // Exclude Google API TypeErrors from error logging
        // e.g. Error: TypeError: undefined is not an object (evaluating 'new window.google.maps')
        //      Error: TypeError: Cannot read properties of undefined (reading 'maps')
        if (!/TypeError/.test(zipError)) {
          // Log Google API error to Heap only
          heapTrackClientError({
            errorName: 'GoogleAPIError',
            errorMessage: `Unexpected error from Google Geocoder API: ${zipError}`,
          });
        }
      }
    }
    // Return true when there isn't a zip mismatch error in formik.values.zip
    return errors.zip !== 'Zip code does not match the state';
  }

  // intercepts DOB input changes to determine whether to show/hide additional income
  const handleDateOfBirthChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    // if the date is valid
    if (validateDate(e.target.value)) {
      // update state to show/hide additional income based on DOB
      // Over 21 show it, under 21 hide it
      setShowAdditionalIncome(
        new Date(e.target.value) <= getMaxDateOfBirthByMinAge(21)
      );
    } else {
      // if we don't have a valid dob, just show additional income
      setShowAdditionalIncome(true);
    }
    // pass the event on to formik for validation
    formik.handleChange(e);
  };

  // Add annualIncome and additionalIncome to get the value to display for the total income
  const calculateTotalIncome = () => {
    const individualIncome =
      parseInt(formik.values.annualIncome.replace(/\D/g, ''), 10) || 0;
    const additionalIncome =
      parseInt(formik.values.additionalIncome.replace(/\D/g, ''), 10) || 0;
    const totalIncome = individualIncome + additionalIncome;
    const formattedTotalIncome = formatCurrencyInputWithCommas(
      totalIncome.toString()
    );
    return formattedTotalIncome;
  };

  // Conditionally show the confirmEmail input after the email input has been focused
  const handleFocusEmail = () => {
    const { checkMatchingZipAndState } = formik.status;
    formik.setStatus({
      showConfirmEmail: true,
      emailHasFocus: true,
      checkMatchingZipAndState,
    });
  };

  // Checks if there is an existing user to sign in...
  // ...any time the email input is unfocused
  const handleBlurEmail = (e: React.FocusEvent<HTMLInputElement>) => {
    // Pass it off to formik to handle validation
    formik.handleBlur(e);
    // Indicate the email is losing focus, but maintain showConfirmEmail as true
    const { checkMatchingZipAndState } = formik.status;
    formik.setStatus({
      showConfirmEmail: true,
      emailHasFocus: false,
      checkMatchingZipAndState,
    });
    // Make sure that this field contains a well-formatted email before checking its existence
    // If it doesn't, skip all of this
    if (!e.target.value || !EMAIL_REGEX.test(e.target.value)) {
      return;
    }
    // Avoid duplicate call if the same value was used in a request triggered from handleChangeEmail
    if (userEmail === e.target.value) {
      return;
    }
    checkEmailExistence(e.target.value);
  };

  // Call getEmailExistenceQuery when the email value is changed via autofill/without blur event
  const handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    // Skip getEmailExistenceQuery here if user is changing value manually
    if (!formik.status.emailHasFocus) {
      // Skip getEmailExistenceQuery if the pre-popped email value is invalid
      if (!e.target.value || !EMAIL_REGEX.test(e.target.value)) {
        return;
      }
      // Skip getEmailExistenceQuery if the email was pre-popped from the login flow
      if (e.target.disabled) {
        return;
      }
      // Changes without focus must check for email existence
      checkEmailExistence(e.target.value);
    }
  };

  // Handles a UI defect caused by the content shift that occurs when the input hints are hidden
  // Only applicable when the screen height is above 1500px (e.g., at 50% zoom or less)
  const handleBlurWithHints = (e: React.FocusEvent<HTMLInputElement>) => {
    if (height > 1500 && e.relatedTarget) {
      // Reinforce click when the input is blurred because the user presses submit
      if (
        submitButtonRef.current &&
        submitButtonRef.current === e.relatedTarget
      ) {
        e.preventDefault();
        submitButtonRef.current.click();
        formik.handleBlur(e);
      }
    } else formik.handleBlur(e);
  };

  // Check if zip/state inputs need to be re-evaluated by isValidZipForState on change
  const handleChangeZipOrState = (e: React.ChangeEvent<any>) => {
    formik.handleChange(e);
    // Skip if the resulting value from the change event is equal to the previous value
    // This happens when the user attempts to input a non-digit character into the zip field
    if (e.target && e.target.name === 'zip') {
      const input = formik.values.zip ?? '';
      const changedInput = e.target.value;
      if (input === changedInput) return;
    }
    if (e.target && (e.target.name === 'zip' || e.target.name === 'state')) {
      const currentZip =
        e.target.name === 'zip' ? e.target.value : formik.values.zip;
      // Only call Geocoding API to validate zip & state when both inputs are valid
      if (formik.values.state !== undefined && validateZip(currentZip)) {
        // Persist unrelated formik.status variables
        const { showConfirmEmail, emailHasFocus } = formik.status;
        formik.setStatus({
          showConfirmEmail,
          emailHasFocus,
          checkMatchingZipAndState: true,
        });
      }
    }
  };

  // Sets all autocomplete address component fields on suggestion click
  const handleAutoComplete = (
    streetAddress: string,
    city: string,
    state: string,
    zipCode: string
  ) => {
    formik.setFieldValue('street1', streetAddress);
    formik.setFieldValue('city', city);
    formik.setFieldValue('state', state);
    formik.setFieldValue('zip', zipCode);
    if (zipCode === '') formik.setFieldTouched('zip', true, true);

    if (formik.values.city === '' && formik.touched.city && city !== '') {
      formik.setFieldTouched('city', false);
    }

    if (formik.values.state === '' && formik.touched.state && state !== '') {
      formik.setFieldTouched('state', false);
    }

    const autoCompleteFields: { [key: string]: string } = {};
    if (streetAddress) autoCompleteFields.street1 = streetAddress;
    if (city) autoCompleteFields.city = city;
    if (state) autoCompleteFields.state = state;
    if (zipCode) autoCompleteFields.zip = zipCode;
    trackChangeOnPrepop(autoCompleteFields);
  };

  // Use synthetic events to track when Address fields are cleared programmatically
  const trackClearAddressFields = () => {
    const clearedValues: { [key: string]: string } = {};
    if (formik.values.street1) clearedValues.street1 = '';
    if (formik.values.street2) clearedValues.street2 = '';
    if (formik.values.city) clearedValues.city = '';
    if (formik.values.state) clearedValues.state = '';
    if (formik.values.zip) clearedValues.zip = '';
    trackChangeOnPrepop(clearedValues);
  };

  // Clears all autocomplete address fields on x-button click
  const clearAddressFields = () => {
    // Track changes on the address fields to be cleared
    trackClearAddressFields();

    formik.setFieldValue('street1', '');
    formik.setFieldValue('street2', '');
    formik.setFieldValue('city', '');
    formik.setFieldValue('state', '');
    formik.setFieldValue('zip', '');

    // Manually set fields to touched to trigger formik validation
    formik.setFieldTouched('street1');
    formik.setFieldTouched('street2');
    formik.setFieldTouched('city');
    formik.setFieldTouched('state');
    formik.setFieldTouched('zip');
  };

  // Start NPC-10717_grey_out_fields
  const isCrcCampaignMatch = (listCrcCampaigns: string, crcValue: string) =>
    listCrcCampaigns
      .split(',')
      .some(
        (crc) => crc.trim().toLowerCase() === crcValue.trim().toLowerCase()
      );

  // when a successful offer code response was registered
  const handleOfferCodeSuccess = (
    prepopFields: OfferCodePrepopFields,
    successfulOfferCode: string
  ) => {
    const offerCodePrepopFields: { [key: string]: string } = {};
    // pre-pop inputs with info gotten from the offer code response
    const crcValue = getCRCValue();
    const crcCampaignMatch = isCrcCampaignMatch(crcCampaigns, crcValue);
    const crcMatch = crcValue && isGreyOutFNAndLNCrcOn && crcCampaignMatch;

    setDisableFirstName(Boolean(crcMatch && prepopFields.firstName));
    setDisableLastName(Boolean(crcMatch && prepopFields.lastName));
    // End NPC-10717_grey_out_fields
    if (prepopFields.firstName) {
      formik.setFieldValue('firstName', prepopFields.firstName);
      offerCodePrepopFields.firstName = prepopFields.firstName;
    }
    if (prepopFields.lastName) {
      formik.setFieldValue('lastName', prepopFields.lastName);
      offerCodePrepopFields.lastName = prepopFields.lastName;
    }
    if (prepopFields.mailingAddressStreet) {
      formik.setFieldValue('street1', prepopFields.mailingAddressStreet);
      offerCodePrepopFields.street1 = prepopFields.mailingAddressStreet;
    }
    if (prepopFields.mailingAddressCity) {
      formik.setFieldValue('city', prepopFields.mailingAddressCity);
      offerCodePrepopFields.city = prepopFields.mailingAddressCity;
    }
    if (prepopFields.mailingAddressState) {
      formik.setFieldValue('state', prepopFields.mailingAddressState);
      offerCodePrepopFields.state = prepopFields.mailingAddressState;
    }
    if (prepopFields.mailingAddressZipCode5) {
      formik.setFieldValue('zip', prepopFields.mailingAddressZipCode5);
      offerCodePrepopFields.zip = prepopFields.mailingAddressZipCode5;
    }
    // hold on to the offer code for when we create the app later
    offerCode.current = successfulOfferCode;
    // hide the modal
    setShowOfferCodeModal(false);
    // Track changes on the fields to be pre-populated from an offer code
    trackChangeOnPrepop(offerCodePrepopFields);
  };

  useEffect(() => {
    abortController = new AbortController();
    if (getJWTFromLocalStorage()) {
      checkForApplicationsByLoggedInUser();
    }
    if (
      isFFidentityEmailOn &&
      getUserIdentityFromSessionStorage() &&
      !getUserIdentityFromSessionStorage().isVerified &&
      window.document.referrer.includes('/auth/signout')
    ) {
      const { firstName, lastName, dateOfBirth } =
        getUserIdentityFromSessionStorage();
      if (firstName) {
        formik.setFieldValue('firstName', firstName);
        prepopLoggedInUserFields.firstName = firstName;
      }
      if (lastName) {
        formik.setFieldValue('lastName', lastName);
        prepopLoggedInUserFields.lastName = lastName;
      }
      if (dateOfBirth) {
        formik.setFieldValue('dateOfBirth', dateOfBirth);
        prepopLoggedInUserFields.dateOfBirth = dateOfBirth;
      }
    }
    fetchLegalDocuments();
    const queryString = decodeURIComponent(window.location.search);
    const crcValue = getCRCValue();
    // Show the offer code modal if offer code crc is present
    const landingPageOfferCode = sessionStorage.getItem(
      LANDING_PAGE_OFFER_CODE_KEY
    );
    if (crcValue && crcValue.endsWith(PROSPER_DM_OFFER_CODE_CAMPAIGN_CODE)) {
      // Display offer code modal for crc values ending with -OC-CC
      setShowOfferCodeModal(
        !landingPageOfferCode || landingPageOfferCode.length !== 8
      );
    }

    // Grey out FN and LN for this CRC StringCRC (read from optimizely rollout variables)

    if (isGreyOutFNAndLNCrcOn && crcValue && crcCampaigns) {
      const isCRCMatch = crcCampaigns
        .split(',')
        .some(
          (crc: string) =>
            crc.toLocaleLowerCase() === crcValue.toLocaleLowerCase()
        );
      if (isCRCMatch) {
        setShowOfferCodeModal(
          !landingPageOfferCode || landingPageOfferCode.length !== 8
        );
      }
    }
    if (landingPageOfferCode) {
      // Pass through session storage offerCode regardless of crc
      setPassedOfferCode(landingPageOfferCode);
      setForceHideModal(
        !crcValue?.endsWith(PROSPER_DM_OFFER_CODE_CAMPAIGN_CODE)
      );
    }
    // Send the initial marketing touch including an offerCode if present
    const deviceType = isMobileUserAgent()
      ? DeviceType.MOBILE_WEB
      : DeviceType.DESKTOP;
    sendMarketingTouch(
      queryString,
      deviceType,
      undefined,
      landingPageOfferCode ?? undefined
    );
    return () => abortController.abort();
  }, []);

  // Watches the current screen height when the window resizes
  useEffect(() => {
    if (!window) return null;
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  // Watch userEmail in SignInContext to clear the email fields and show the SignInModal when needed
  useEffect(() => {
    if (userEmail) {
      // Show SignInModal when getEmailExistenceQuery response indicates this is an existing user
      setShowSignInModal(true);
      // Track SignInModal visibility
      heapTrackElementRendered('SignInModal');
    } else if (!userEmail) {
      // Clear email fields if the user chooses to continue with a different email from SignInModal
      clearEmailFields();
    }
  }, [userEmail]);

  // Route to OfferWaitingRoom when the app is first created
  useEffect(() => {
    // Application ID will be truthy only upon successful application creation
    if (applicationId) {
      // Route to OfferWaitingRoom
      navigate(prefixRoute(`/application/${applicationId}/offer-waiting-room`));
    }
  }, [applicationId]);

  useEffect(() => {
    if (dsaFFVariant === 'control')
      formik.setFieldValue('isDsaEnrolled', false);
  }, [dsaFFVariant]);

  // Wait until the form is rendered before tracking form prepop events for loggedInUsers
  useEffect(() => {
    if (!loading && !checkingForExistingUser && prepopOnLoadRef.current) {
      trackChangeOnPrepop(prepopOnLoadRef.current);
      prepopOnLoadRef.current = null;
    }
  }, [loading, checkingForExistingUser, prepopOnLoadRef.current]);

  // When formik is submitting/not submitting
  useEffect(() => {
    // Visual order of form fields in the UI
    const orderedFields = [
      'firstName',
      'lastName',
      'dateOfBirth',
      'email',
      'confirmEmail',
      'ssn',
      'street1',
      'street2',
      'city',
      'state',
      'zip',
      'housingPayment',
      'incomeSourceTypes',
      'annualIncome',
      'additionalIncome',
      'isDsaEnrolled',
      'phoneNumber',
      'hasConsentedToTcpa',
      'eSign',
    ];
    // if we've stopped submitting, but the form isn't valid
    if (!formik.isSubmitting && !formik.isValid) {
      // scroll to the first error
      scrollToErrors(
        Object.keys(formik.errors),
        orderedFields,
        Object.keys(formik.touched)
      );
    }
  }, [formik.isSubmitting]);

  // Render ---------------------------------------------------------------------------------------
  if (loading || checkingForExistingUser || formik.isSubmitting) {
    return <ApplicationLoader />;
  }

  if (showFailure) return <ApplicationError />;

  if (isFFidentityEmailOn && (showDOBIdentity || showDOBAndSSNIdentity)) {
    return (
      <ApplicationIdentity
        user={loggedInUser}
        showDOBIdentity={showDOBIdentity}
        showDOBAndSSNIdentity={showDOBAndSSNIdentity}
        showIdentityError={showIdentityError}
        setShowDOBIdentity={setShowDOBIdentity}
        setShowDOBAndSSNIdentity={setShowDOBAndSSNIdentity}
        onError={() => setShowIdentityError(true)}
      />
    );
  }

  return (
    <>
      <Nav showSkipLink />
      <Page>
        <CompletedApplicationModal
          showModal={showCompletedApplicationModal}
          setShowModal={setShowCompletedApplicationModal}
          isMigrationNeeded={migrationNeeded}
          email={formik.values.email}
        />
        <SignInModal
          showModal={showSignInEmailOnBlurModal}
          setShowModal={setShowSignInEmailOnBlurModal}
        />
        <OfferCodeModal
          forceHideModal={forceHideModal}
          showModal={showOfferCodeModal}
          setShowModal={setShowOfferCodeModal}
          passedOfferCode={passedOfferCode}
          onSuccess={(prepopFields, successfulOfferCode) => {
            handleOfferCodeSuccess(prepopFields, successfulOfferCode);
          }}
        />
        <Body>
          <CreditCardInfo
            firstName={formik.values.firstName}
            lastName={formik.values.lastName}
          />
          <Card>
            <ApplyHeadingWrapper>
              <ApplyHeading />
            </ApplyHeadingWrapper>
            <Main id="main" aria-labelledby="form-subheading">
              <Form
                id="npc-apply-single-page-form"
                onSubmit={formik.handleSubmit}
              >
                <FormSection>
                  <AboutYouWrapper>
                    <FormSectionHeading id="form-subheading">
                      Tell us about yourself
                    </FormSectionHeading>
                    {/* show tooltip if user logged in or feature flag on */}
                    {isFFidentityEmailOn &&
                      getJWTFromLocalStorage() &&
                      disableFirstName && (
                        <ToolTip
                          id="apply-tooltip"
                          isGrayInfoIcon
                          heading="Why can't I update my info?"
                          body="In order to keep your account safe, changes aren't allowed to personal identifiable information. If you have any questions, <a style='color: #4D4D4D;' href='/contact-us'>contact us</a>."
                        />
                      )}
                  </AboutYouWrapper>
                  <FullWidthRow>
                    <HalfWidthInputsContainer>
                      <HalfWidthInput>
                        <TextInput
                          name="firstName"
                          value={formik.values.firstName}
                          label="First name"
                          error={formik.errors.firstName}
                          touched={formik.touched.firstName}
                          disabled={formik.isSubmitting || disableFirstName}
                          maxLength={40}
                          customPattern={NAME_REGEX}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          forwardRef={inputRefs.firstName}
                        />
                      </HalfWidthInput>
                      <HalfWidthInput>
                        <TextInput
                          name="lastName"
                          value={formik.values.lastName}
                          label="Last name"
                          error={formik.errors.lastName}
                          touched={formik.touched.lastName}
                          disabled={formik.isSubmitting || disableLastName}
                          maxLength={40}
                          customPattern={NAME_REGEX}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          forwardRef={inputRefs.lastName}
                        />
                      </HalfWidthInput>
                    </HalfWidthInputsContainer>
                  </FullWidthRow>
                  <FullWidthRow>
                    <DateTextInput
                      name="dateOfBirth"
                      value={formik.values.dateOfBirth}
                      label="Date of birth (MM/DD/YYYY)"
                      error={formik.errors.dateOfBirth}
                      touched={formik.touched.dateOfBirth}
                      disabled={formik.isSubmitting || disableDOB}
                      onChange={handleDateOfBirthChange}
                      onBlur={formik.handleBlur}
                      forwardRef={inputRefs.dateOfBirth}
                    />
                  </FullWidthRow>
                  <FullWidthRow>
                    <TextInput
                      name="email"
                      type="email"
                      value={formik.values.email}
                      label="Email address"
                      error={formik.errors.email}
                      touched={formik.touched.email}
                      disabled={formik.isSubmitting || disableEmail}
                      maxLength={50}
                      onChange={handleChangeEmail}
                      onFocus={handleFocusEmail}
                      onBlur={handleBlurEmail}
                      forwardRef={inputRefs.email}
                    />
                  </FullWidthRow>
                  {(formik.status.showConfirmEmail ||
                    formik.values.email ||
                    formik.touched.email) && (
                    <FullWidthRow>
                      <TextInput
                        name="confirmEmail"
                        type="email"
                        value={formik.values.confirmEmail}
                        label="Re-enter email address"
                        error={formik.errors.confirmEmail}
                        touched={formik.touched.confirmEmail}
                        disabled={formik.isSubmitting || disableEmail}
                        maxLength={50}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        onPaste={(e) => e.preventDefault()}
                        forwardRef={inputRefs.confirmEmail}
                      />
                    </FullWidthRow>
                  )}
                  <FullWidthRow>
                    <SsnTextInput
                      name="ssn"
                      value={formik.values.ssn}
                      label="Social Security number"
                      error={formik.errors.ssn}
                      touched={formik.touched.ssn}
                      disabled={formik.isSubmitting || disableSSN}
                      onChange={formik.handleChange}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        if (
                          e &&
                          e.target &&
                          e.target.value === '' &&
                          e.relatedTarget === document.getElementById('toggle')
                        ) {
                          // Focus street1 when ssn input is blurred without a value
                          // (workaround to support keyboard navigation in Firefox)
                          const street1 = document.getElementById('street1');
                          if (street1) street1.focus();
                        }
                      }}
                      setFieldTouched={(e) => {
                        formik.setFieldTouched('ssn');
                        if (e) {
                          // Focus street1 when visibility toggle is blurred
                          // (workaround to support keyboard navigation in Firefox)
                          const street1 = document.getElementById('street1');
                          if (street1) e.relatedTarget = street1;
                        }
                      }}
                      showInfoOnFocus
                      showSecuredCopy
                    />
                  </FullWidthRow>
                </FormSection>

                <FormSection>
                  <FormSectionHeading>Your Address</FormSectionHeading>
                  <FullWidthRow>
                    <AddressTextInput
                      name="street1"
                      value={formik.values.street1}
                      label="Home address"
                      type="text"
                      error={formik.errors.street1}
                      touched={formik.touched.street1}
                      disabled={formik.isSubmitting}
                      maxLength={50}
                      customPattern={ADDRESS_REGEX}
                      resetLabel={
                        formik.touched.street1 && !formik.values.street1
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onAutoComplete={handleAutoComplete}
                      onClear={clearAddressFields}
                      forwardRef={inputRefs.street1}
                    />
                  </FullWidthRow>
                  <FullWidthRow>
                    <TextInput
                      name="street2"
                      value={formik.values.street2}
                      label="Apartment, suite, unit, etc."
                      error={formik.errors.street2}
                      touched={formik.touched.street2}
                      disabled={formik.isSubmitting}
                      maxLength={50}
                      customPattern={ADDRESS_REGEX}
                      resetLabel={
                        formik.touched.street2 && !formik.values.street2
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      required={false}
                      forwardRef={inputRefs.street2}
                    />
                  </FullWidthRow>
                  <FullWidthRow>
                    <TextInput
                      name="city"
                      value={formik.values.city}
                      label="City"
                      error={formik.errors.city}
                      touched={formik.touched.city}
                      disabled={formik.isSubmitting}
                      maxLength={50}
                      customPattern={NAME_REGEX}
                      resetLabel={formik.touched.city && !formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      forwardRef={inputRefs.city}
                    />
                  </FullWidthRow>
                  <FullWidthRow>
                    <HalfWidthInputsContainer>
                      <HalfWidthInput>
                        <DropDown
                          name="state"
                          value={formik.values.state}
                          label="State"
                          options={STATES}
                          error={formik.errors.state}
                          touched={formik.touched.state}
                          disabled={formik.isSubmitting}
                          resetLabel={
                            formik.touched.state && !formik.values.state
                          }
                          onChange={handleChangeZipOrState}
                          onBlur={formik.handleBlur}
                          forwardRef={inputRefs.state}
                        />
                      </HalfWidthInput>
                      <HalfWidthInput>
                        <ZipCodeTextInput
                          name="zip"
                          type="tel"
                          value={formik.values.zip}
                          label="Zip code"
                          error={formik.errors.zip}
                          touched={formik.touched.zip}
                          disabled={formik.isSubmitting}
                          maxLength={10}
                          resetLabel={formik.touched.zip && !formik.values.zip}
                          onChange={handleChangeZipOrState}
                          onBlur={formik.handleBlur}
                          forwardRef={inputRefs.zip}
                        />
                      </HalfWidthInput>
                    </HalfWidthInputsContainer>
                  </FullWidthRow>
                  <FullWidthRow>
                    <CurrencyTextInput
                      name="housingPayment"
                      value={formik.values.housingPayment}
                      label="Monthly rent or mortgage"
                      error={formik.errors.housingPayment}
                      touched={formik.touched.housingPayment}
                      disabled={formik.isSubmitting}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      maxLength={6}
                      forwardRef={inputRefs.housingPayment}
                    />
                  </FullWidthRow>
                </FormSection>

                <FormSection>
                  <IncomeSectionHeading id="incomeSectionHeading">
                    What type of income do you receive?
                  </IncomeSectionHeading>
                  <FullWidthRow>
                    <MultiSelectInput
                      name="incomeSourceTypes"
                      value={formik.values.incomeSourceTypes}
                      heading="Select all that apply"
                      options={INCOME_SOURCE_TYPES}
                      error={formik.errors.incomeSourceTypes}
                      touched={formik.touched.incomeSourceTypes}
                      disabled={formik.isSubmitting}
                      ariaLabelledBy="incomeSectionHeading"
                      onChange={formik.handleChange}
                      setFieldValue={formik.setFieldValue}
                    />
                  </FullWidthRow>
                  <FullWidthRow>
                    <CurrencyTextInput
                      name="annualIncome"
                      value={formik.values.annualIncome}
                      label="Yearly individual income"
                      error={formik.errors.annualIncome}
                      touched={formik.touched.annualIncome}
                      disabled={formik.isSubmitting}
                      onChange={formik.handleChange}
                      onBlur={(e) => handleBlurWithHints(e)}
                      hintContents={<YearlyIncomeHintContents />}
                      maxLength={9}
                      forwardRef={inputRefs.annualIncome}
                    />
                  </FullWidthRow>
                  {showAdditionalIncome && (
                    <>
                      <FullWidthRow>
                        <OptionalTextInput
                          actionText="+ add household income (optional)"
                          show={formik.values.additionalIncome.length > 0}
                        >
                          <CurrencyTextInput
                            name="additionalIncome"
                            value={formik.values.additionalIncome}
                            label="Household income (optional)"
                            error={formik.errors.additionalIncome}
                            touched={formik.touched.additionalIncome}
                            disabled={formik.isSubmitting}
                            onChange={formik.handleChange}
                            onBlur={(e) => handleBlurWithHints(e)}
                            hintContents={<AdditionalIncomeHintContents />}
                            maxLength={9}
                            required={false}
                            data-testid="household-income-input"
                            forwardRef={inputRefs.additionalIncome}
                          />
                        </OptionalTextInput>
                      </FullWidthRow>
                      {formik.values.additionalIncome.length > 0 && (
                        <FullWidthRow>
                          <TotalIncomeRow>
                            <TotalIncomeLabel>
                              Total combined income:
                            </TotalIncomeLabel>
                            <TotalIncomeValue>{`$${calculateTotalIncome()}`}</TotalIncomeValue>
                          </TotalIncomeRow>
                        </FullWidthRow>
                      )}
                    </>
                  )}
                </FormSection>

                <ApplicationDSA
                  isDsaEnabled={formik.values.isDsaEnrolled}
                  error={formik.errors.isDsaEnrolled}
                  onDsaSelected={(isDsa: boolean) => {
                    formik.setFieldValue('isDsaEnrolled', isDsa);
                  }}
                />

                <FormSection>
                  <FormSectionHeading>Contact Information</FormSectionHeading>
                  <FullWidthRow>
                    <PhoneNumberInput
                      name="phoneNumber"
                      value={formik.values.phoneNumber}
                      label="Phone number"
                      error={formik.errors.phoneNumber}
                      touched={formik.touched.phoneNumber}
                      disabled={formik.isSubmitting}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      forwardRef={inputRefs.phoneNumber}
                    />
                  </FullWidthRow>
                  {isNewTcpaEnabled ? (
                    <TcpaModal
                      title={getDocumentTitle(
                        LegalDocuments.TCPA_AUTHORIZATION_HTML
                      )}
                      contentUrl={getViewUrl(
                        LegalDocuments.TCPA_AUTHORIZATION_HTML
                      )}
                      showModal={showTcpaModal}
                      setShowModal={setShowTcpaModal}
                      onDisagree={() => {
                        setAcceptedTcpa(false);
                        setShowTcpaModal(false);
                      }}
                    />
                  ) : (
                    <FullWidthRow>
                      <CheckBox
                        name="hasConsentedToTcpa"
                        value={formik.values.hasConsentedToTcpa}
                        required={false}
                        setFieldValue={formik.setFieldValue}
                        setFieldTouched={() =>
                          formik.setFieldTouched('hasConsentedToTcpa')
                        }
                        disabled={formik.isSubmitting}
                        error={formik.errors.hasConsentedToTcpa}
                        touched={formik.touched.hasConsentedToTcpa}
                        onChange={formik.handleChange}
                        ariaLabelledBy="tcpa-checkbox-text"
                      >
                        {legalDocuments.length > 0 && applyTCPACheckboxCopy()}
                      </CheckBox>
                    </FullWidthRow>
                  )}
                </FormSection>

                <SchumerSection>
                  <FormSectionHeading>Terms and Conditions</FormSectionHeading>
                  <SchumerBoxContainer
                    src={getViewUrl(LegalDocuments.TERMS_AND_CONDITIONS)}
                  />
                  <SchumerNotice>{SCHUMER_NOTICE_COPY}</SchumerNotice>
                  <CheckBox
                    name="eSign"
                    value={formik.values.eSign}
                    setFieldValue={formik.setFieldValue}
                    setFieldTouched={() => formik.setFieldTouched('eSign')}
                    disabled={formik.isSubmitting}
                    error={formik.errors.eSign}
                    touched={formik.touched.eSign}
                    onChange={formik.handleChange}
                    ariaLabelledBy="esign-checkbox-text"
                  >
                    {legalDocuments.length > 0 && (
                      <ESignCheckboxCopy
                        isNewTcpaEnabled={isNewTcpaEnabled}
                        acceptedTcpa={acceptedTcpa}
                        formik={formik}
                        setShowTcpaModal={setShowTcpaModal}
                        getViewUrl={getViewUrl}
                        getDocumentTitle={getDocumentTitle}
                      />
                    )}
                  </CheckBox>
                </SchumerSection>

                <SubmitButton
                  id="submit"
                  aria-label="Submit application to see my offer"
                  type="submit"
                  disabled={formik.isSubmitting}
                  ref={submitButtonRef}
                >
                  See my offer
                </SubmitButton>

                <SubmitText>
                  Checking your offer won’t affect your credit score
                </SubmitText>
              </Form>
            </Main>
          </Card>
        </Body>

        <Footer onApplyPage />
      </Page>
    </>
  );
};
